"use client";

import { useState } from "react";
import { BadgeCheck, ChevronRight } from "lucide-react";

export type FAQItem = {
  question: string;
  answer: string;
};

export const defaultFAQItems: FAQItem[] = [
  {
    question: "Do you offer a free trial?",
    answer: "Yes, you can try all plans free for 7 days. No credit card is required to get started.",
  },
  {
    question: "Do I need a credit card to get started?",
    answer: "No, you can start your trial without entering payment details.",
  },
  {
    question: "Can I upgrade or downgrade my plan anytime?",
    answer: "Yes, you can change plans whenever your catalog, usage, or team needs change.",
  },
  {
    question: "How does billing work?",
    answer: "Plans are billed on your selected cycle, and any plan changes are handled automatically.",
  },
  {
    question: "What happens if I cancel my subscription?",
    answer: "You keep access until the end of the current billing period and can reactivate later.",
  },
  {
    question: "Are there any hidden fees?",
    answer: "No, the price shown for each plan is the price you pay. Add-ons are shown before checkout.",
  },
  {
    question: "What's included in each plan?",
    answer: "Each plan includes the features listed in the pricing table, with higher tiers adding more usage and team capabilities.",
  },
];

type FAQSectionProps = {
  id?: string;
  eyebrow?: string;
  title?: string;
  description?: string;
  faqs?: FAQItem[];
};

export default function FAQSection({
  id = "faq",
  eyebrow = "FAQs",
  title = "Everything You Need to Know",
  description = "Everything you need to know about pricing",
  faqs = defaultFAQItems,
}: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id={id} className="flex flex-col items-start gap-12 bg-white px-6 py-16 sm:px-10 lg:px-[150px] lg:py-20">
      <div data-testid="faq-content" className="mx-auto flex w-full max-w-[1140px] flex-col items-start gap-12">
        <div className="flex w-full flex-col items-start gap-5">
          <div className="flex h-9 w-[100px] items-center justify-center gap-2.5 rounded-[21px] border border-[#E0EAF4] px-4 py-2">
            <BadgeCheck aria-hidden="true" className="h-5 w-5 shrink-0 text-[#1464FF]" strokeWidth={1.8} />
            <span
              style={{
                fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                fontSize: 15,
                lineHeight: "18px",
                fontWeight: 600,
                color: "#1B1C30",
              }}
            >
              {eyebrow}
            </span>
          </div>
          <h2
            className="w-full text-[32px] leading-[42px] sm:text-[40px] sm:leading-[52px]"
            style={{
              fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
              fontWeight: 700,
              color: "#1B1C30",
              letterSpacing: 0,
            }}
          >
            {title}
          </h2>
          <p
            className="w-full text-[15px] leading-5"
            style={{
              fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
              color: "#4D4D5C",
            }}
          >
            {description}
          </p>
        </div>

        <div className="flex w-full flex-col items-start gap-4">
          {faqs.map((faq, i) => (
            <div key={faq.question} className="w-full border-b border-[#E0EAF4]">
              <button
                type="button"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="group flex min-h-[77px] w-full items-center justify-between gap-6 px-0 py-6 text-left sm:px-6"
              >
                <span
                  className="min-w-0 flex-1 text-[18px] leading-6 sm:text-[22px] sm:leading-[29px]"
                  style={{
                    fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                    fontWeight: 600,
                    color: openIndex === i ? "#1464FF" : "#1B1C30",
                    transition: "color 0.2s",
                  }}
                >
                  {faq.question}
                </span>
                <ChevronRight
                  aria-hidden="true"
                  className="h-6 w-6 shrink-0 rotate-90 transition-transform duration-200"
                  color={openIndex === i ? "#1464FF" : "#4D4D5C"}
                  strokeWidth={1.5}
                  style={{
                    transform: openIndex === i ? "rotate(-90deg)" : "rotate(90deg)",
                  }}
                />
              </button>
              <div
                className="grid overflow-hidden transition-[grid-template-rows,opacity] duration-300"
                style={{
                  gridTemplateRows: openIndex === i ? "1fr" : "0fr",
                  opacity: openIndex === i ? 1 : 0,
                }}
              >
                <div className="min-h-0">
                  <p
                    className="pb-6 pr-10 text-[15px] leading-[25px] sm:px-6 sm:pr-16"
                    style={{
                      fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                      color: "#4D4D5C",
                    }}
                  >
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
